import get from "lodash/get";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../../../utils/format-sub";
import BandTag from "./BandTag";
import CsvExportLink from "./CsvExportLink";
import SortableHeader from "./SortableHeader";

// tipKey: the header's explainer (income.plans.tooltips.*). Period tips
// name the selected range, so "Revenue" says which months it covers.
const SNAPSHOT_COLUMNS = [
  {
    key: "activeCount",
    labelKey: "income.plans.activeMembers",
    tipKey: "activeMembers",
  },
  { key: "mrr", labelKey: "income.plans.mrr", tipKey: "mrr" },
  { key: "trialCount", labelKey: "income.plans.onTrial", tipKey: "onTrial" },
  // All-time, not "now" — but it isn't a period figure either; its tip says so.
  { key: "ltv", labelKey: "income.plans.ltv", tipKey: "ltv" },
];

const PERIOD_COLUMNS = [
  {
    key: "activeAtStart",
    labelKey: "income.plans.activeAtStart",
    tipKey: "activeAtStart",
  },
  { key: "newInPeriod", labelKey: "income.plans.new", tipKey: "new" },
  {
    key: "cancelledInPeriod",
    labelKey: "income.plans.churned",
    tipKey: "churned",
  },
  {
    key: "activeAtEnd",
    labelKey: "income.plans.activeAtEnd",
    tipKey: "activeAtEnd",
  },
  { key: "churnRate", labelKey: "income.plans.churnRate", tipKey: "churnRate" },
  {
    key: "revenueInPeriod",
    labelKey: "income.plans.revenue",
    tipKey: "revenue",
  },
  { key: "arpu", labelKey: "income.plans.arpu", tipKey: "arpu" },
];

// A plan whose price is archived in Stripe but still has paying/trialing
// members — a coach can't sell it anymore, but it's still live revenue and
// stays visible by default. An archived plan with nobody left on it is the
// only thing "Include archive" needs to reveal.
const isLegacy = (plan) =>
  plan.archived && (plan.activeCount || 0) + (plan.trialCount || 0) > 0;

const PlansTable = ({
  plans = [],
  currency = "usd",
  csvString = "",
  showProduct = false,
  asOfLabel = "",
  periodLabel = "",
  rangeLabel = "",
}) => {
  const { t } = useTranslation("coaching-portal");
  const [sortConfig, setSortConfig] = useState({
    key: "revenueInPeriod",
    direction: "descending",
  });
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [legacyOnly, setLegacyOnly] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);

  const money = (value) => formatNumber(value || 0, currency);
  const planPrice = (plan) =>
    plan.interval && plan.amount != null
      ? `${money(plan.amount)}/${t(`income.plans.interval.${plan.interval}`, {
          count: plan.intervalCount || 1,
        })}`
      : null;

  const visiblePlans = useMemo(() => {
    const query = search.trim().toLowerCase();
    return plans.filter((plan) => {
      if (!includeArchived && plan.archived && !isLegacy(plan)) return false;
      if (activeOnly && !(plan.activeCount > 0)) return false;
      if (legacyOnly && !isLegacy(plan)) return false;
      if (query && !(plan.planName || "").toLowerCase().includes(query))
        return false;
      return true;
    });
  }, [plans, search, activeOnly, legacyOnly, includeArchived]);

  // Revenue from archived plans that "Include archive" hides, so the visible
  // rows can be reconciled with the Overview's net revenue (sc-46065).
  const hiddenArchivedRevenue = includeArchived
    ? 0
    : plans
        .filter((plan) => plan.archived && !isLegacy(plan))
        .reduce((total, plan) => total + (plan.revenueInPeriod || 0), 0);

  const sortedPlans = useMemo(() => {
    const sorted = [...visiblePlans];
    sorted.sort((a, b) => {
      const aVal = get(a, sortConfig.key);
      const bVal = get(b, sortConfig.key);
      if (aVal === bVal) return 0;
      // null activeCount (pass rows) always sorts last, regardless of direction.
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return sortConfig.direction === "ascending" ? -1 : 1;
      return sortConfig.direction === "ascending" ? 1 : -1;
    });
    return sorted;
  }, [visiblePlans, sortConfig]);

  const handleSort = (key, direction) => setSortConfig({ key, direction });

  const columnCount =
    1 + (showProduct ? 1 : 0) + SNAPSHOT_COLUMNS.length + PERIOD_COLUMNS.length;

  return (
    <>
      <header className="table-header">
        <hgroup>
          <h3 className="title">{t("income.plans.tableTitle")}</h3>
        </hgroup>
        <div className="flex gap-4 items-center">
          <button
            type="button"
            className={`filter-button${includeArchived ? " active" : ""}`}
            onClick={() => setIncludeArchived((prev) => !prev)}
          >
            {t("income.plans.includeArchived")}
          </button>
          <CsvExportLink
            csv={csvString}
            filename="plan-performance.csv"
            t={t}
          />
        </div>
      </header>
      <hr className="mt-3" />
      <div className="plans-table-filters">
        <button
          type="button"
          className={`filter-button${activeOnly ? " active" : ""}`}
          onClick={() => setActiveOnly((prev) => !prev)}
        >
          {t("income.plans.activeOnlyFilter")}
        </button>
        <button
          type="button"
          className={`filter-button${legacyOnly ? " active" : ""}`}
          onClick={() => setLegacyOnly((prev) => !prev)}
        >
          {t("income.plans.legacyFilter")}
        </button>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("income.plans.searchPlaceholder")}
        />
      </div>
      <hr className="mt-3 mb-0!" />
      {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
      <table role="report-table" className="plans-table">
        {/* biome-ignore lint/a11y/useValidAriaRole: table-header used for structure */}
        <thead role="table-header">
          <tr>
            {showProduct && <th className="bg-transparent!" />}
            <th className="bg-transparent!" />
            <th className="bg-transparent!" colSpan={SNAPSHOT_COLUMNS.length}>
              <div className="flex items-center justify-center gap-2">
                <BandTag
                  kind="snapshot"
                  label={t("income.bands.snapshot")}
                  meta={asOfLabel}
                />
              </div>
            </th>
            <th className="bg-transparent!" colSpan={PERIOD_COLUMNS.length}>
              <div className="flex items-center justify-center gap-2">
                <BandTag
                  kind="period"
                  label={t("income.bands.period")}
                  meta={periodLabel}
                />
              </div>
            </th>
          </tr>
          <tr>
            {showProduct && <th>{t("income.plans.product")}</th>}
            <SortableHeader
              column={{ key: "planName", labelKey: "income.plans.plan" }}
              sortConfig={sortConfig}
              onSort={handleSort}
              t={t}
              className="text-left!"
            />
            {SNAPSHOT_COLUMNS.map((column) => (
              <SortableHeader
                key={column.key}
                column={column}
                sortConfig={sortConfig}
                onSort={handleSort}
                tooltip={t(`income.plans.tooltips.${column.tipKey}`, {
                  range: rangeLabel,
                })}
                t={t}
                className="snapshot-col"
              />
            ))}
            {PERIOD_COLUMNS.map((column) => (
              <SortableHeader
                key={column.key}
                column={column}
                sortConfig={sortConfig}
                onSort={handleSort}
                tooltip={t(`income.plans.tooltips.${column.tipKey}`, {
                  range: rangeLabel,
                })}
                t={t}
              />
            ))}
          </tr>
        </thead>
        {sortedPlans.length > 0 ? (
          <tbody>
            {sortedPlans.map((plan) => (
              <tr key={plan.priceId}>
                {showProduct && (
                  <td>{plan.product || t("income.plans.unattributed")}</td>
                )}
                <td className="text-left!">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <span>{plan.planName || plan.priceId}</span>
                      {plan.isPass && (
                        <span className="pill">
                          {t("income.plans.passBadge")}
                        </span>
                      )}
                    </div>
                    {(planPrice(plan) || isLegacy(plan)) && (
                      <small className="font-normal text-(--white-70) text-xs">
                        {[
                          planPrice(plan),
                          isLegacy(plan) && t("income.plans.legacyBadge"),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </small>
                    )}
                  </div>
                </td>
                <td className="snapshot-col">
                  {plan.activeCount == null
                    ? t("income.plans.unitsSold", {
                        count: plan.unitsSold,
                      })
                    : plan.activeCount}
                </td>
                <td className="snapshot-col">{money(plan.mrr)}</td>
                <td className="snapshot-col">{plan.trialCount || 0}</td>
                <td className="snapshot-col">{money(plan.ltv)}</td>
                <td>{plan.activeAtStart}</td>
                <td>{plan.newInPeriod}</td>
                <td>{plan.cancelledInPeriod}</td>
                <td>{plan.activeAtEnd}</td>
                <td>
                  {plan.churnRate == null ? (
                    // Say why on hover (sc-46067).
                    <span title={t("income.plans.smallSample")}>—</span>
                  ) : (
                    `${plan.churnRate}%`
                  )}
                </td>
                <td>{money(plan.revenueInPeriod)}</td>
                <td>{money(plan.arpu)}</td>
              </tr>
            ))}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td colSpan={columnCount}>
                <div className="body-table-empty">
                  <p>
                    {plans.length
                      ? t("income.plans.noResults")
                      : t("income.plans.empty")}
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        )}
      </table>
      {Math.abs(hiddenArchivedRevenue) >= 0.01 && (
        <p className="plans-table-footnote">
          {t("income.plans.archivedHiddenNote", {
            amount: money(hiddenArchivedRevenue),
          })}
        </p>
      )}
      <p className="plans-table-footnote">{t("income.plans.footnote")}</p>
    </>
  );
};

export default PlansTable;
